import React, { useEffect, useRef, useState, useLayoutEffect } from 'react';
interface Tab {
  id: string;
  label: string;
  mobileLabel?: string;
  icon?: React.ReactNode;
  content?: React.ReactNode;
  badge?: string | number;
}
interface TabsProps {
  tabs: Tab[];
  defaultTab?: string;
  activeTab?: string;
  onChange?: (tabId: string) => void;
  children?: React.ReactNode;
  variant?: 'underline' | 'pills';
}
export function Tabs({
  tabs,
  defaultTab,
  activeTab: controlledActiveTab,
  onChange,
  children,
  variant = 'underline'
}: TabsProps) {
  const [internalActiveTab, setInternalActiveTab] = useState(defaultTab || tabs[0]?.id || '');
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [indicatorStyle, setIndicatorStyle] = useState({
    left: 0,
    width: 0
  });
  const navRef = useRef<HTMLDivElement>(null);
  const activeTabRef = useRef<HTMLButtonElement>(null);
  const btnRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const activeTab = controlledActiveTab !== undefined ? controlledActiveTab : internalActiveTab;
  const setActiveTab = (tabId: string) => {
    if (onChange) onChange(tabId);else setInternalActiveTab(tabId);
  };

  // Sliding indicator
  const updateIndicator = () => {
    if (variant !== 'underline') return;
    const btn = btnRefs.current[activeTab];
    const nav = navRef.current;
    if (!btn || !nav) return;
    const navRect = nav.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    setIndicatorStyle({
      left: btnRect.left - navRect.left + nav.scrollLeft,
      width: btnRect.width
    });
  };
  useLayoutEffect(updateIndicator, [activeTab, tabs, variant]);
  const checkScroll = () => {
    const el = navRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    checkScroll();
    const ro = new ResizeObserver(() => {
      checkScroll();
      updateIndicator();
    });
    ro.observe(el);
    el.addEventListener('scroll', checkScroll, {
      passive: true
    });
    return () => {
      ro.disconnect();
      el.removeEventListener('scroll', checkScroll);
    };
  }, [tabs]);
  useEffect(() => {
    btnRefs.current[activeTab]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center'
    });
  }, [activeTab]);
  const activeTabContent = tabs.find(t => t.id === activeTab)?.content;
  if (variant === 'pills') {
    return <div data-id="element-5504">
        <div className="flex gap-1 overflow-x-auto pb-1" style={{
        scrollbarWidth: 'none'
      }} data-id="element-5505">
          {tabs.map(tab => {
          const isActive = activeTab === tab.id;
          return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`
                  flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium whitespace-nowrap
                  transition-all duration-200 flex-shrink-0
                  ${isActive ? 'bg-gradient-to-r from-[#1BACA6] to-[#1890C8] text-white shadow-md shadow-cyan-500/20' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.07]'}
                `} data-id="element-5506">
                
                {tab.icon && <span className="flex-shrink-0" data-id="element-5507">{tab.icon}</span>}
                <span data-id="element-5508">{tab.label}</span>
                {tab.badge !== undefined && <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${isActive ? 'bg-white/20 text-white' : 'bg-gray-200 dark:bg-white/[0.1] text-gray-600 dark:text-gray-400'}`} data-id="element-5509">
                    {tab.badge}
                  </span>}
              </button>;
        })}
        </div>
        <div className="pt-6" data-id="element-5510">{activeTabContent || children}</div>
      </div>;
  }
  return <div data-id="element-5511">
      <div className="relative border-b border-gray-200 dark:border-white/[0.08]" data-id="element-5512">
        {canScrollLeft && <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-8 z-10 bg-gradient-to-r from-white dark:from-gray-900 to-transparent" data-id="element-5513" />}
        {canScrollRight && <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-10 z-10 bg-gradient-to-l from-white dark:from-gray-900 to-transparent" data-id="element-5514" />}

        <nav ref={navRef} className="flex overflow-x-auto relative" style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        WebkitOverflowScrolling: 'touch'
      } as React.CSSProperties} data-id="element-5515">
          
          {/* Sliding indicator */}
          <span className="absolute bottom-0 h-0.5 bg-gradient-to-r from-[#1BACA6] to-[#1890C8] rounded-full transition-all duration-250 ease-out" style={{
          left: indicatorStyle.left,
          width: indicatorStyle.width
        }} data-id="element-5516" />
          

          {tabs.map(tab => {
          const isActive = activeTab === tab.id;
          return <button key={tab.id} ref={el => {
            btnRefs.current[tab.id] = el;
            if (isActive) (activeTabRef.current as any) = el;
          }} onClick={() => setActiveTab(tab.id)} className={`
                  group relative flex items-center gap-1.5 px-3 sm:px-4 py-3
                  text-xs sm:text-sm font-medium whitespace-nowrap
                  -mb-px transition-all duration-150 flex-shrink-0
                  focus:outline-none
                  ${isActive ? 'text-cyan-600 dark:text-cyan-400' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}
                `} data-id="element-5517">
                
                {tab.icon && <span className={`flex-shrink-0 transition-colors ${isActive ? 'text-cyan-500' : 'text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300'}`} data-id="element-5518">
                    {tab.icon}
                  </span>}
                {tab.mobileLabel ? <>
                    <span className="sm:hidden" data-id="element-5519">{tab.mobileLabel}</span>
                    <span className="hidden sm:inline" data-id="element-5520">{tab.label}</span>
                  </> : <span className="max-w-[80px] sm:max-w-none truncate sm:overflow-visible" data-id="element-5521">{tab.label}</span>}
                {tab.badge !== undefined && <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold transition-colors ${isActive ? 'bg-cyan-100 dark:bg-cyan-500/10 text-cyan-600 dark:text-cyan-400' : 'bg-gray-100 dark:bg-white/[0.07] text-gray-500 dark:text-gray-400'}`} data-id="element-5522">
                    {tab.badge}
                  </span>}
              </button>;
        })}
        </nav>
      </div>

      <div className="pt-6 animate-fade-in" data-id="element-5523">{activeTabContent || children}</div>
    </div>;
}
interface TabPanelProps {
  id: string;
  activeTab: string;
  children: React.ReactNode;
}
export function TabPanel({
  id,
  activeTab,
  children
}: TabPanelProps) {
  if (id !== activeTab) return null;
  return <div className="animate-fade-in" data-id="element-5524">{children}</div>;
}